import { useEffect, useState } from 'react';
import { Link, Outlet, createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AuthGate } from '../features/auth/auth-gate.js';
import { useAuth } from '../features/auth/use-auth.js';
import { CommandPalette } from '../components/command-palette.js';
import { ShortcutsDialog } from '../components/shortcuts-dialog.js';
import {
  APPROVAL_BATCH_COUNT_QUERY_KEY,
  countAwaitingApprovalBatches,
} from '../features/approvals/api.js';
import { HestiaLogo } from '../components/hestia-logo.js';
import { useRecentItems, type RecentItem } from '../features/navigation/recent-items.js';
import { NotificationsBell } from '../features/notifications/notifications-bell.js';

interface NavItemProps {
  to: string;
  label: string;
  icon: React.ReactNode;
  badge?: number | string;
  exact?: boolean;
  collapsed?: boolean;
}

function NavItem({ to, label, icon, badge, exact, collapsed }: NavItemProps) {
  // Collapsed mode: icon-only square + a hand-rolled hover tooltip that
  // pops to the right of the icon. We drop the native `title` so we don't
  // get the browser's slow ugly bubble alongside our custom one. Active
  // state still tints the cell; text + badge text are hidden, but a tiny
  // dot pip remains so counters keep their signal.
  const base =
    'nav-tip group relative flex h-9 items-center rounded-md text-body font-medium text-[var(--fg-muted)] transition-colors hover:bg-[var(--app-hover)]';
  const activeBase =
    'nav-tip group relative flex h-9 items-center rounded-md text-body font-semibold text-[var(--brand-800)] bg-[var(--app-selected)]';
  const padding = collapsed ? 'w-9 justify-center px-0' : 'gap-2.5 px-3';
  return (
    <Link
      to={to}
      aria-label={collapsed ? label : undefined}
      // Native title as a robust fallback for the collapsed-sidebar tooltip:
      // the CSS `.nav-tip::after` bubble pops out to the right of the icon, but
      // the nav's `overflow-y-auto` scroll container clips it (overflow-x is
      // forced to auto too), so the styled tooltip never showed collapsed. The
      // browser renders `title` at OS level, unclipped.
      title={collapsed ? label : undefined}
      // The dashboard route is "/app" — without exact matching, TanStack
      // Router treats it as active for every /app/* child route, so both
      // Dashboard and the current section light up at the same time.
      {...(exact ? { activeOptions: { exact: true } } : {})}
      {...(collapsed ? { 'data-tip': label } : {})}
      className={`${base} ${padding}`}
      activeProps={{ className: `${activeBase} ${padding}` }}
    >
      <span className="inline-flex text-[var(--fg-muted)] group-[.font-semibold]:text-[var(--brand-600)]">
        {icon}
      </span>
      {!collapsed && <span className="flex-1">{label}</span>}
      {badge != null &&
        (collapsed ? (
          <span
            className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-[var(--brand-500)]"
            aria-label={`${badge} értesítés`}
          />
        ) : (
          <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[var(--app-divider-2)] px-1.5 text-meta font-semibold leading-none text-[var(--fg-muted)] tn">
            {badge}
          </span>
        ))}
    </Link>
  );
}

/**
 * Admin section of the sidebar. Expanded sidebar: a collapsible "Admin"
 * group (chevron header + child links) so the five admin entries don't
 * crowd the main nav; auto-opens when an /app/admin route is active.
 * Collapsed (icon-only) rail: a divider + the flat icon list, since a
 * fly-out submenu doesn't fit a 36px rail.
 */
function AdminNavGroup({ collapsed }: { collapsed: boolean }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const adminActive = pathname.startsWith('/app/admin');
  const [open, setOpen] = useState(adminActive);
  // Navigating into an admin route (e.g. via the command palette) should
  // reveal the group even if the user had collapsed it.
  useEffect(() => {
    if (adminActive) setOpen(true);
  }, [adminActive]);

  const items: Array<{ to: string; label: string; icon: React.ReactNode }> = [
    { to: '/app/admin/usage', label: 'Felhasználás', icon: Icon.chart },
    { to: '/app/admin/jobs', label: 'Folyamatok', icon: Icon.jobs },
    { to: '/app/admin/ai', label: 'AI beállítások', icon: Icon.ai },
    { to: '/app/admin/emails', label: 'E-mail sablonok', icon: Icon.mail },
    { to: '/app/admin/media-vocab', label: 'Címkeszótár', icon: Icon.tag },
    { to: '/app/admin/users', label: 'Csapat', icon: Icon.users },
    { to: '/app/admin/api-tokens', label: 'Gépi tokenek', icon: Icon.key },
  ];

  if (collapsed) {
    return (
      <>
        <div className="mx-1.5 my-3 h-px bg-[var(--app-divider)]" aria-label="Admin szekció" />
        {items.map((it) => (
          <NavItem key={it.to} to={it.to} label={it.label} icon={it.icon} collapsed />
        ))}
      </>
    );
  }

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`group relative flex h-9 w-full items-center gap-2.5 rounded-md px-3 text-body font-medium transition-colors hover:bg-[var(--app-hover)] ${
          adminActive ? 'text-[var(--brand-800)]' : 'text-[var(--fg-muted)]'
        }`}
      >
        <span className="inline-flex">{Icon.settings}</span>
        <span className="flex-1 text-left">Admin</span>
        <svg
          width={16}
          height={16}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 200ms' }}
        >
          <polyline points="9 6 15 12 9 18" />
        </svg>
      </button>
      {open && (
        <div className="mt-0.5 space-y-0.5 border-l border-[var(--app-divider)] pl-2">
          {items.map((it) => (
            <NavItem key={it.to} to={it.to} label={it.label} icon={it.icon} collapsed={false} />
          ))}
        </div>
      )}
    </div>
  );
}

/* Inline Lucide-style icons (1.75 stroke) */
const Icon = {
  settings: (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  dashboard: (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </svg>
  ),
  hotel: (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 21h18" />
      <path d="M5 21V7l7-4 7 4v14" />
      <path d="M9 21v-6h6v6" />
      <path d="M9 9h.01" />
      <path d="M15 9h.01" />
      <path d="M9 12h.01" />
      <path d="M15 12h.01" />
    </svg>
  ),
  calendar: (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 9h18" />
      <path d="M8 3v4" />
      <path d="M16 3v4" />
    </svg>
  ),
  chart: (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 3v18h18" />
      <path d="M7 14l4-4 4 4 5-5" />
    </svg>
  ),
  users: (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  key: (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z" />
      <circle cx="16.5" cy="7.5" r=".5" fill="currentColor" />
    </svg>
  ),
  ai: (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 3v4M3 5h4M19 17v4M17 19h4M13 3l2.5 7.5L23 13l-7.5 2.5L13 23l-2.5-7.5L3 13l7.5-2.5L13 3z" />
    </svg>
  ),
  search: (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  ),
  approval: (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 12l2 2 4-4" />
      <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c2.43 0 4.64.96 6.27 2.53" />
    </svg>
  ),
  jobs: (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 8v4l3 2" />
      <circle cx="12" cy="12" r="9" />
    </svg>
  ),
  inbox: (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  ),
  reserve: (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  ),
  content: (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 3v5h5" />
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M8 12h8" />
      <path d="M8 16h8" />
    </svg>
  ),
  mail: (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M2 8l10 6 10-6" />
    </svg>
  ),
  approved: (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 2h6a1 1 0 0 1 1 1v1h1a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1V3a1 1 0 0 1 1-1z" />
      <path d="M9 13l2 2 4-4" />
    </svg>
  ),
  tag: (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12.59 2.59a2 2 0 0 0-1.42-.59H4a2 2 0 0 0-2 2v7.17a2 2 0 0 0 .59 1.41l8 8a2 2 0 0 0 2.82 0l7.17-7.17a2 2 0 0 0 0-2.82Z" />
      <circle cx="7.5" cy="7.5" r="1.5" />
    </svg>
  ),
};

/**
 * Count for the sidebar badge, from ONE org-wide request.
 *
 * This used to walk the graph client-side: list hotels, then one `/plans?hotelId`
 * per hotel, then one `/approval-batches?planId` per plan — 1 + N + M requests on
 * every page in the app, for a single number. Because the rate limiter buckets per
 * handler, those M calls all landed in the same bucket and were the main reason
 * `/app/plans` answered 429.
 *
 * `GET /approval-batches` without a planId already returns the org-wide list, and
 * the query key is shared with the approvals page, so navigating between them
 * costs nothing extra.
 */
function useActiveBatchCount(): number {
  const countQ = useQuery({
    queryKey: APPROVAL_BATCH_COUNT_QUERY_KEY,
    queryFn: countAwaitingApprovalBatches,
    staleTime: 30_000,
  });
  return countQ.data ?? 0;
}

function recentKindLabel(kind: RecentItem['kind']): string {
  switch (kind) {
    case 'hotel':
      return 'Szálloda';
    case 'plan':
      return 'Terv';
    case 'post':
      return 'Poszt';
  }
}

function RecentItemsBlock() {
  const items = useRecentItems();
  if (items.length === 0) return null;
  return (
    <div className="border-t border-[var(--app-divider)] px-2 py-3">
      <div className="mb-1.5 px-2 text-meta font-semibold uppercase tracking-wider text-[var(--fg-faint)]">
        Utoljára szerkesztett
      </div>
      <ul className="space-y-0.5">
        {items.map((it) => (
          <li key={`${it.kind}-${it.id}`}>
            <Link
              to={it.to}
              title={it.label}
              className="group flex flex-col rounded-md px-2 py-1.5 text-meta text-[var(--fg-muted)] transition-colors hover:bg-[var(--app-hover)] hover:text-[var(--ink-700)]"
            >
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-1 w-1 rounded-full bg-[var(--brand-400)] opacity-70" />
                <span className="truncate font-medium">{it.label}</span>
              </span>
              <span className="ml-2.5 truncate text-meta text-[var(--fg-faint)]">
                {recentKindLabel(it.kind)}
                {it.context ? ` · ${it.context}` : ''}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AppShell() {
  const { user, logout } = useAuth();
  const initials =
    user?.firstName || user?.lastName
      ? `${(user.lastName ?? '')[0] ?? ''}${(user.firstName ?? '')[0] ?? ''}`.toUpperCase() ||
        (user.email ?? '?').slice(0, 2).toUpperCase()
      : (user?.email ?? '?').slice(0, 2).toUpperCase();
  const displayName =
    user?.firstName || user?.lastName
      ? `${user.lastName ?? ''} ${user.firstName ?? ''}`.trim()
      : (user?.email ?? '');
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const activeBatches = useActiveBatchCount();
  const navigate = useNavigate();

  // Sidebar collapse — persisted across reloads via localStorage. SSR-safe
  // init (returns false on the server, then rehydrates from storage on the
  // first client render).
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('hestia.sidebarCollapsed') === '1';
  });
  useEffect(() => {
    window.localStorage.setItem('hestia.sidebarCollapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  // Theme — three states:
  //   'system' (default) — follow the OS `prefers-color-scheme` live
  //   'light' / 'dark'   — explicit user override, persisted in localStorage
  // The toggle in the topbar cycles through system → light → dark → system,
  // so the user can pin a mode if they want or leave it on auto.
  type ThemePref = 'system' | 'light' | 'dark';
  const [themePref, setThemePref] = useState<ThemePref>(() => {
    if (typeof window === 'undefined') return 'system';
    const stored = window.localStorage.getItem('hestia.theme');
    if (stored === 'dark' || stored === 'light' || stored === 'system') return stored;
    return 'system';
  });
  // Live OS preference — re-evaluated when the user flips macOS appearance.
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    function onChange(e: MediaQueryListEvent) {
      setSystemPrefersDark(e.matches);
    }
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const theme: 'light' | 'dark' =
    themePref === 'system' ? (systemPrefersDark ? 'dark' : 'light') : themePref;
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.setAttribute('data-theme', 'dark');
    else root.removeAttribute('data-theme');
  }, [theme]);
  useEffect(() => {
    window.localStorage.setItem('hestia.theme', themePref);
  }, [themePref]);

  // Atmosphere gating (D4): the decorative mint mesh + dot-grid + vignette
  // (body::before/::after) add visual load behind dense data screens. Quiet
  // them on the calendar and the content table by toggling data-quiet-bg on
  // <body>; everywhere else (dashboard, hotels, approvals, login) keeps the
  // texture. The plan-list (/app/plans) and /app/plans/new are NOT dense, so
  // only the actual month calendar (/app/plans/:hotelId/:year/:month) opts out.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    const quiet =
      pathname.startsWith('/app/content') ||
      (pathname.startsWith('/app/plans/') && pathname !== '/app/plans/new');
    if (quiet) document.body.setAttribute('data-quiet-bg', '1');
    else document.body.removeAttribute('data-quiet-bg');
    return () => document.body.removeAttribute('data-quiet-bg');
  }, [pathname]);

  useEffect(() => {
    function isInTextInput(el: EventTarget | null): boolean {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName.toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
    }
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
        return;
      }
      // ⌘/Ctrl + \ toggles the sidebar — standard SaaS shortcut (Linear,
      // Notion, VSCode). Cheap to add, expected by power users.
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
        e.preventDefault();
        setCollapsed((c) => !c);
        return;
      }
      // Plain '/' opens the palette — matches GitHub, Discord, Slack. Only
      // when the user isn't typing into a real input.
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !isInTextInput(e.target)) {
        e.preventDefault();
        setPaletteOpen(true);
        return;
      }
      // '?' opens the shortcuts modal — every screen has it now, no need
      // to take the user out to the profile page just to read the list.
      if (e.key === '?' && !e.metaKey && !e.ctrlKey && !isInTextInput(e.target)) {
        e.preventDefault();
        setShortcutsOpen(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  return (
    <AuthGate>
      {/* No solid bg here — the atmosphere layer on body::before composites
          through. Components that need an opaque surface use --app-surface
          / --app-sidebar tokens. */}
      <div className="h-app flex min-h-screen">
        {/* Sidebar — width animates between 240px (full) and 60px (icon).
            transition-[width] keeps content inside the rail still while
            the rail itself collapses, which feels less janky than
            re-rendering the whole shell. */}
        <aside
          className={`sticky top-0 flex h-screen flex-shrink-0 flex-col border-r border-[var(--app-divider)] bg-[var(--app-sidebar)] transition-[width] duration-200 ${
            collapsed ? 'w-[60px]' : 'w-[240px]'
          }`}
        >
          {/* Logo + collapse toggle + org — toggle sits at the top next to
              the logo (expanded) or as a standalone button under the mark
              (collapsed) so the user always sees a clear control. */}
          <div className={`py-4 ${collapsed ? 'px-2' : 'px-4'}`}>
            <div
              className={`flex items-center ${
                collapsed ? 'flex-col gap-3' : 'justify-between gap-2'
              }`}
            >
              <HestiaLogo size={collapsed ? 24 : 28} markOnly={collapsed} />
              <button
                type="button"
                onClick={() => setCollapsed((c) => !c)}
                title={collapsed ? 'Menü kibontása (⌘\\)' : 'Menü összecsukása (⌘\\)'}
                aria-label={collapsed ? 'Menü kibontása' : 'Menü összecsukása'}
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] text-[var(--fg-muted)] shadow-sm transition-colors hover:border-[var(--brand-400)] hover:bg-[var(--app-hover)] hover:text-[var(--ink-700)]"
              >
                <svg
                  width={14}
                  height={14}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.75}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  style={{
                    transform: collapsed ? 'rotate(180deg)' : 'none',
                    transition: 'transform 200ms',
                  }}
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
            </div>
            {!collapsed && (
              <div className="mt-3.5 flex h-8 items-center gap-2 rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] px-2.5 text-meta font-medium">
                <span className="flex h-[18px] w-[18px] items-center justify-center rounded-sm bg-[var(--brand-500)] text-meta font-bold text-white">
                  M
                </span>
                {/* The brand is "Morgens" — the workspace label used to read
                    "Morgens Agency", which is not a name we use. */}
                <span className="flex-1 text-left text-[var(--ink-700)]">Morgens</span>
              </div>
            )}
          </div>

          {/* Scrollable middle region — holds the primary nav AND the
              recent-items block so they scroll together when the sidebar
              content overflows the viewport. The header above (logo +
              toggle) and footer below (role badge) stay pinned. */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            <nav className={`space-y-0.5 py-3 ${collapsed ? 'px-2.5' : 'px-2'}`}>
              <NavItem
                to="/app"
                label="Dashboard"
                icon={Icon.dashboard}
                exact
                collapsed={collapsed}
              />
              <NavItem to="/app/hotels" label="Hotelek" icon={Icon.hotel} collapsed={collapsed} />
              <NavItem to="/app/plans" label="Tervek" icon={Icon.calendar} collapsed={collapsed} />
              <NavItem
                to="/app/content"
                label="Tartalmak"
                icon={Icon.content}
                collapsed={collapsed}
              />
              <NavItem
                to="/app/approved"
                label="Jóváhagyott"
                icon={Icon.approved}
                collapsed={collapsed}
              />
              <NavItem
                to="/app/approvals"
                label="Jóváhagyások"
                icon={Icon.approval}
                collapsed={collapsed}
                {...(activeBatches > 0 ? { badge: activeBatches } : {})}
              />
              <NavItem
                to="/app/inbox"
                label="Értesítések"
                icon={Icon.inbox}
                collapsed={collapsed}
              />

              {user?.role === 'admin' && <AdminNavGroup collapsed={collapsed} />}
            </nav>

            {!collapsed && <RecentItemsBlock />}
          </div>
          {/* Footer: quiet org/role badge (expanded only) — collapse toggle
              now lives at the top, so the footer stays minimal. */}
          {!collapsed && (
            <div className="border-t border-[var(--app-divider)] px-4 py-3">
              <div className="flex min-w-0 items-center gap-2 text-meta text-[var(--fg-faint)]">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--brand-500)]" />
                <span className="truncate">
                  {user?.role === 'admin'
                    ? 'Admin'
                    : user?.role === 'editor'
                      ? 'Szerkesztő'
                      : 'Olvasó'}{' '}
                  · Morgens
                </span>
              </div>
            </div>
          )}
        </aside>

        {/* Main column */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Topbar — transparent so the atmosphere layer flows through.
              No bottom border and no surface fill: the topbar visually
              belongs to the page, not as a separate horizontal band. */}
          <header className="flex h-[60px] items-center px-6">
            <div className="mx-auto flex w-full max-w-[1240px] items-center gap-3">
              <button
                type="button"
                onClick={() => setPaletteOpen(true)}
                className="flex h-9 max-w-[420px] flex-1 items-center gap-2 rounded-md border border-[var(--app-divider-2)] bg-[var(--app-surface)] px-3 text-left text-body text-[var(--fg-2)] shadow-sm transition-colors hover:border-[var(--brand-400)] hover:text-[var(--ink-700)] focus:border-[var(--brand-500)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-500)]/20"
                aria-label="Keresés megnyitása"
              >
                {Icon.search}
                <span className="flex-1 text-[var(--fg-faint)]">
                  Keresés szállodákban, tervekben…
                </span>
                <kbd className="rounded border border-[var(--app-divider-2)] bg-[var(--app-surface-2)] px-1.5 py-0.5 font-mono text-meta text-[var(--fg-faint)]">
                  ⌘K
                </kbd>
              </button>
              {/* Profile chip styled as a clear pill-button — border + surface
                  so it reads as actionable from the first glance. */}
              <Link
                to="/app/profile"
                title="Profilom"
                className="ml-auto flex h-9 items-center gap-2 rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] px-2.5 shadow-sm transition-colors hover:border-[var(--brand-400)] hover:bg-[var(--app-surface-2)]"
              >
                <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[var(--brand-100)] text-meta font-semibold text-[var(--brand-800)]">
                  {initials}
                </span>
                <span className="hidden text-body font-medium text-[var(--ink-700)] sm:inline">
                  {displayName || user?.email}
                </span>
              </Link>
              <NotificationsBell />
              <button
                type="button"
                onClick={() => setShortcutsOpen(true)}
                title="Billentyűparancsok (?)"
                aria-label="Billentyűparancsok megnyitása"
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] text-[var(--fg-muted)] shadow-sm transition-colors hover:border-[var(--brand-400)] hover:text-[var(--ink-700)]"
              >
                <svg
                  width={16}
                  height={16}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.75}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 1-1 1.7v.5" />
                  <circle cx="12" cy="17" r="0.5" fill="currentColor" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() =>
                  setThemePref((t) =>
                    t === 'system' ? 'light' : t === 'light' ? 'dark' : 'system',
                  )
                }
                title={
                  themePref === 'system'
                    ? `Téma: rendszer szerint (${theme === 'dark' ? 'sötét' : 'világos'})`
                    : themePref === 'light'
                      ? 'Téma: világos (kattints sötétre)'
                      : 'Téma: sötét (kattints rendszer-követőre)'
                }
                aria-label={`Téma váltás — jelenleg ${themePref}`}
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] text-[var(--fg-muted)] shadow-sm transition-colors hover:border-[var(--brand-400)] hover:text-[var(--ink-700)]"
              >
                {themePref === 'system' ? (
                  // Half-sun-half-moon icon — auto mode.
                  <svg
                    width={16}
                    height={16}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.75}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 3a9 9 0 0 0 0 18z" fill="currentColor" stroke="none" />
                  </svg>
                ) : themePref === 'dark' ? (
                  <svg
                    width={16}
                    height={16}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.75}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                  </svg>
                ) : (
                  <svg
                    width={16}
                    height={16}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.75}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="4" />
                    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                  </svg>
                )}
              </button>
              <button
                type="button"
                onClick={() => void logout()}
                title="Kijelentkezés"
                aria-label="Kijelentkezés"
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] text-[var(--fg-muted)] shadow-sm transition-colors hover:border-[var(--brand-400)] hover:text-[var(--ink-700)]"
              >
                <svg
                  width={16}
                  height={16}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.75}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <path d="M16 17l5-5-5-5" />
                  <path d="M21 12H9" />
                </svg>
              </button>
            </div>
          </header>

          {/* Page content */}
          <main className="flex-1 overflow-auto px-6 py-6">
            <div className="mx-auto max-w-[1240px]">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onShowShortcuts={() => {
          setPaletteOpen(false);
          setShortcutsOpen(true);
        }}
      />
      <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </AuthGate>
  );
}

export const Route = createFileRoute('/app')({
  component: AppShell,
});
